import React, { FC, useEffect } from 'react';
import NukaCarousel from 'nuka-carousel';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

import withBreakpoints, {
  InjectedProps as WithBreakpointsProps,
} from 'lib/withBreakpoints';

import { ModuleHeader } from 'components/modules';
import ArticleItem from './ArticleItem';
import TopicItem from './TopicItem';
import ImageItem from './ImageItem';
import TextItem from './TextItem';
import VideoItem from './VideoItem';
import QuoteItem from './QuoteItem';
import { Button } from 'components/base';
import { LeftArrowInsideCircle, RightArrowInsideCircle } from 'styled';
import {
  Container,
  Header,
  Carousel,
  CardContainer,
  Card,
  ControlArrow,
} from './style';

import {
  GeneralModuleColor,
  GeneralModuleColorScheme,
} from 'constants/ColorScheme';
import { Color } from 'styled/theme/colors';
import Language from 'constants/Language';

import { CarouselModule as ICarouselModule, CarouselItem } from 'types';

type PassedProps = {
  module: ICarouselModule;
  applyArticleBodyWidthOffset?: boolean;
};

type Props = WithBreakpointsProps & PassedProps;

const renderCarouselItemByType = (
  item: CarouselItem,
  bgColor: Color,
  textColor: Color
) => {
  if (!item) return null;

  switch (item.type) {
    case 'article':
      return (
        <ArticleItem item={item} bgColor={bgColor} textColor={textColor} />
      );
    case 'textItem':
      return <TextItem item={item} bgColor={bgColor} textColor={textColor} />;
    case 'quoteItem':
      return <QuoteItem item={item} bgColor={bgColor} textColor={textColor} />;
    case 'imageItem':
      return <ImageItem item={item} bgColor={bgColor} textColor={textColor} />;
    case 'videoItem':
      return <VideoItem item={item} bgColor={bgColor} textColor={textColor} />;
    case 'topic':
      return <TopicItem item={item} textColor={textColor} />;
    default:
      return null;
  }
};

const MOBILE_CAROUSEL_WIDTH = '326px';
const DESKTOP_CAROUSEL_WIDTH = '462px';

const LeftControls = ({ previousSlide }: { previousSlide: () => void }) => (
  <ControlArrow
    as={Button}
    m={1}
    ariaLabel={Language.t('CarouselModule.previousButtonAriaLabel')}
    onClick={previousSlide}
  >
    <LeftArrowInsideCircle />
  </ControlArrow>
);

const RightControls = ({ nextSlide }: { nextSlide: () => void }) => (
  <ControlArrow
    as={Button}
    m={1}
    ariaLabel={Language.t('CarouselModule.nextButtonAriaLabel')}
    onClick={nextSlide}
  >
    <RightArrowInsideCircle />
  </ControlArrow>
);

/*
 * NukaCarousel has a bug where the carousel's initial height is not properly set,
 * We fix this by triggering the lib's internal `setDimensions` function
 * by dispatching a window resize event.
 */
const dispatchResizeEvent = () => window.dispatchEvent(new Event('resize'));

const CarouselModule: FC<Props> = ({
  module,
  currentBreakpoint,
  applyArticleBodyWidthOffset,
}) => {
  // Trigger resize event on load
  useEffect(() => {
    setTimeout(dispatchResizeEvent, 2000);
  }, []);

  // Trigger resize event on scroll, throttled at 400ms
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const userHasScrolled = currPos.y > prevPos.y;

      if (userHasScrolled) {
        dispatchResizeEvent();
      }
    },
    [dispatchResizeEvent],
    undefined,
    true,
    400
  );

  const { moduleHeader, moduleOptions } = module;
  const colorScheme =
    GeneralModuleColorScheme[moduleOptions.color as GeneralModuleColor];
  const breakpointIsMdDown = ['EXTRA_SMALL', 'SMALL', 'MEDIUM'].includes(
    currentBreakpoint
  );

  return (
    <Container applyArticleBodyWidthOffset={applyArticleBodyWidthOffset}>
      <Header
        as={ModuleHeader}
        title={moduleHeader.title}
        description={moduleHeader.description}
      />
      <Carousel
        as={NukaCarousel}
        cellSpacing={0}
        slideWidth={
          breakpointIsMdDown ? MOBILE_CAROUSEL_WIDTH : DESKTOP_CAROUSEL_WIDTH
        }
        heightMode="max"
        defaultControlsConfig={{
          pagingDotsStyle: { display: 'none' },
        }}
        enableKeyboardControls={true}
        swiping={true}
        wrapAround={true}
        renderCenterLeftControls={breakpointIsMdDown ? null : LeftControls}
        renderCenterRightControls={breakpointIsMdDown ? null : RightControls}
      >
        {module.carouselItems.map((item, i) => {
          if (!item) return null;

          const hasEvenIndex = i % 2 === 0;
          const bgColor =
            colorScheme.background === 'white'
              ? 'white'
              : hasEvenIndex
              ? colorScheme.background
              : colorScheme.darkBackground;
          const textColor = colorScheme.text;

          return (
            <CardContainer key={item.id}>
              <Card
                bgColor={bgColor}
                textColor={textColor}
                p={[1.5, 1.5, 1.5, 2.25]}
              >
                {renderCarouselItemByType(item, bgColor, textColor)}
              </Card>
            </CardContainer>
          );
        })}
      </Carousel>
    </Container>
  );
};

export default withBreakpoints(CarouselModule);
